import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { DocMentionPreview } from "@/modules/comments/components/DocMentionPreview";
import {
  docMentionPath,
  type DocMentionKind,
} from "@/modules/comments/mention-math";
import { DETAIL_COMPACT_MQ } from "@/modules/shell/detail-edit-mode";
import { shouldOpenRelatedDocPreview } from "./related-doc-link-math";

/**
 * Related SO/PO/product number → mini preview · View more (plan/45).
 * Plain desk click peeks; ⌘/Ctrl/middle-click and phone navigate.
 */
export function RelatedDocLink(props: {
  companySlug: string;
  kind: DocMentionKind;
  docId: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DETAIL_COMPACT_MQ).matches
      : true,
  );
  const to = docMentionPath(props.companySlug, props.kind, props.docId);

  useEffect(() => {
    const mq = window.matchMedia(DETAIL_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  function onClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!shouldOpenRelatedDocPreview(e, compact)) return;
    e.preventDefault();
    setOpen(true);
  }

  return (
    <>
      <DocNumberLink to={to} className={props.className} onClick={onClick}>
        {props.children}
      </DocNumberLink>
      <DocMentionPreview
        open={open && !compact}
        companySlug={props.companySlug}
        kind={props.kind}
        docId={props.docId}
        onOpenChange={setOpen}
      />
    </>
  );
}
